import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs';
import { BlogAPI } from '../api/api';

function BlogCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
      <div className="aspect-[16/10] bg-[var(--color-inerva-mint)]" />
      <div className="p-6">
        <div className="flex gap-3 mb-3">
          <div className="w-16 h-5 bg-[var(--color-inerva-mint)] rounded-full" />
          <div className="w-20 h-5 bg-[var(--color-inerva-mint)] rounded-full" />
        </div>
        <div className="h-6 bg-[var(--color-inerva-mint)] rounded mb-3 w-3/4" />
        <div className="space-y-2">
          <div className="h-4 bg-[var(--color-inerva-mint)] rounded w-full" />
          <div className="h-4 bg-[var(--color-inerva-mint)] rounded w-2/3" />
        </div>
      </div>
    </div>
  );
}

function BlogCard({ article, getSummary }) {
  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300"
    >
      <article className="h-full flex flex-col">
        <div className="aspect-[16/10] bg-[var(--color-inerva-mint)] overflow-hidden relative">
          {article.imagenPortada ? (
            <>
              <img
                src={article.imagenPortada}
                alt={article.titulo}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg
                className="w-16 h-16 text-[var(--color-inerva-forest)] opacity-20"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                />
              </svg>
            </div>
          )}
        </div>

        <div className="p-6 flex flex-col flex-grow">
          <div className="flex items-center gap-3 text-xs text-[var(--color-inerva-text-light)] mb-3">
            {article.categoria && (
              <span className="px-2.5 py-1 bg-[var(--color-inerva-blush)] rounded-full font-medium">
                {article.categoria}
              </span>
            )}
            <span>
              {new Date(article.fechaPublicacion).toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </span>
          </div>

          <h2 className="text-lg font-serif mb-3 text-[var(--color-inerva-forest)] group-hover:text-[var(--color-inerva-forest-light)] transition-colors">
            {article.titulo}
          </h2>

          <p className="text-sm text-[var(--color-inerva-text-light)] flex-grow line-clamp-3">
            {getSummary(article)}
          </p>

          <div className="mt-4 pt-4 border-t border-[var(--color-inerva-mint)]/30">
            <span className="text-sm font-medium text-[var(--color-inerva-forest)] inline-flex items-center gap-2 group-hover:gap-3 transition-all">
              Leer artículo
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}

export default function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('Todos');

  const categories = ['Todos', 'General', 'Psicología', 'Bienestar', 'Terapia'];

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await BlogAPI.getAll();
        const articlesWithImages = data.map((article) => ({
          ...article,
          imagenPortada: article.imagenPortada
            ? `data:image/jpeg;base64,${article.imagenPortada}`
            : null,
        }));
        setArticles(articlesWithImages);
      } catch (err) {
        setError('No se pudieron cargar los artículos. Intenta de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const getSummary = (article, maxLength = 150) => {
    if (article.extracto) return article.extracto;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = article.contenido;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    if (text.length > maxLength) return text.substring(0, maxLength) + '...';
    return text;
  };

  const filtered = activeCategory === 'Todos'
    ? articles
    : articles.filter((a) => a.categoria === activeCategory);

  return (
    <>
      <Helmet>
        <title>Blog | Inerva Psicología</title>
        <meta
          name="description"
          content="Artículos sobre bienestar emocional, salud mental, ansiedad, estrés y herramientas para tu día a día."
        />
        <link rel="canonical" href="https://www.inervapsicologia.es/blog" />
      </Helmet>

      <Header />

      <main className="min-h-screen bg-[var(--color-inerva-cream)] pt-40 pb-20">
        <div className="container-narrow">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium tracking-widest text-[var(--color-inerva-forest)] opacity-70 mb-4 uppercase">
              Blog
            </span>
            <h1 className="mb-4">Reflexiones y recursos</h1>
            <p className="text-[var(--color-inerva-text-light)] max-w-2xl mx-auto">
              Artículos sobre bienestar emocional, terapia y herramientas para cuidar de tu salud mental.
            </p>
          </div>

          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mb-10">
            <TabsList className="flex-wrap justify-center mx-auto w-fit">
              {categories.map((cat) => (
                <TabsTrigger key={cat} value={cat}>
                  {cat}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {loading && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <BlogCardSkeleton key={i} />
              ))}
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((article) => (
                <BlogCard key={article.id} article={article} getSummary={getSummary} />
              ))}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && articles.length > 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--color-inerva-mint)] flex items-center justify-center">
                <svg className="w-8 h-8 text-[var(--color-inerva-forest)] opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <p className="text-[var(--color-inerva-text-light)]">
                No hay artículos en esta categoría.
              </p>
            </div>
          )}

          {!loading && !error && articles.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[var(--color-inerva-mint)] flex items-center justify-center">
                <svg className="w-10 h-10 text-[var(--color-inerva-forest)] opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-serif mb-2">Próximamente</h3>
              <p className="text-[var(--color-inerva-text-light)]">
                Estoy preparando contenido para ti. ¡Vuelve pronto!
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}
